// Foundational Stage Curricular Goals Schema
// Based on NIPUN Bharat / NEP 2020 framework for Grade 1 (Foundational Stage)
// Scores: 1 = Beginner | 2 = Progressing | 3 = Proficient

export interface CGIndicator {
  entryKey: string;
  entryLabel: string;
}

export interface CurricularGoal {
  s_no: number;
  domain: string;
  curricularGoal: string;
  label: string;
  assessment: CGIndicator[];
}

export const FOUNDATIONAL_SCHEMA: CurricularGoal[] = [
  {
    s_no: 1,
    domain: 'Physical Development',
    curricularGoal: 'Children develop habits that keep them healthy and safe',
    label: 'CG-1: Children develop habits that keep them healthy and safe',
    assessment: [
      { entryKey: 'C_1_1', entryLabel: 'Shows a liking for and understanding of nutritious food and does not waste food' },
      { entryKey: 'C_1_2', entryLabel: 'Practices basic self-care and hygiene' },
      { entryKey: 'C_1_3', entryLabel: 'Keeps school/classroom hygienic and organised' },
      { entryKey: 'C_1_4', entryLabel: 'Practices safe use of material and simple tools' },
      { entryKey: 'C_1_5', entryLabel: 'Shows awareness of safety in movements (walking, running, cycling) and acts appropriately' },
      { entryKey: 'C_1_6', entryLabel: 'Understands unsafe situations and asks for help' },
    ],
  },
  {
    s_no: 2,
    domain: 'Physical Development',
    curricularGoal: 'Children develop sharpness in sensorial perceptions',
    label: 'CG-2: Children develop sharpness in sensorial perceptions',
    assessment: [
      { entryKey: 'C_2_1', entryLabel: 'Differentiates between shapes, colours, and their shades' },
      { entryKey: 'C_2_2', entryLabel: 'Develops visual memory for symbols and representations' },
      { entryKey: 'C_2_3', entryLabel: 'Differentiates sounds and sound patterns by their pitch, volume, and tempo' },
      { entryKey: 'C_2_4', entryLabel: 'Differentiates multiple smells and tastes' },
      { entryKey: 'C_2_5', entryLabel: 'Develops discrimination in the sense of touch' },
      { entryKey: 'C_2_6', entryLabel: 'Begins integrating sensorial perceptions to get a holistic awareness of their experiences' },
    ],
  },
  {
    s_no: 3,
    domain: 'Physical Development',
    curricularGoal: 'Children develop a fit and flexible body',
    label: 'CG-3: Children develop a fit and flexible body',
    assessment: [
      { entryKey: 'C_3_1', entryLabel: 'Shows coordination between sensorial perceptions and body movements in various activities' },
      { entryKey: 'C_3_2', entryLabel: 'Shows balance, coordination, and flexibility in various physical activities' },
      { entryKey: 'C_3_3', entryLabel: 'Shows precision and control in working with their hands and fingers' },
      { entryKey: 'C_3_4', entryLabel: 'Shows strength and endurance in carrying, walking, and running' },
    ],
  },
  {
    s_no: 4,
    domain: 'Socio-Emotional & Ethical Development',
    curricularGoal: 'Children develop emotional intelligence — ability to understand and manage their own emotions, and responds positively to social norms',
    label: 'CG-4: Children develop emotional intelligence',
    assessment: [
      { entryKey: 'C_4_1', entryLabel: "Starts recognising 'self' as an individual belonging to a family and community" },
      { entryKey: 'C_4_2', entryLabel: 'Recognises different emotions and makes deliberate efforts to regulate them appropriately' },
      { entryKey: 'C_4_3', entryLabel: 'Interacts comfortably with other children and adults' },
      { entryKey: 'C_4_4', entryLabel: 'Shows cooperative behaviour with other children' },
      { entryKey: 'C_4_5', entryLabel: 'Understands and responds positively to social norms in the classroom and school' },
      { entryKey: 'C_4_6', entryLabel: 'Shows kindness and helpfulness to others (including animals, plants) when they are in need' },
      { entryKey: 'C_4_7', entryLabel: 'Understands and responds positively to different thoughts, preferences, and emotional needs of other children' },
    ],
  },
  {
    s_no: 5,
    domain: 'Socio-Emotional & Ethical Development',
    curricularGoal: "Children develop a positive attitude towards productive work and service or 'Seva'",
    label: "CG-5: Children develop a positive attitude towards productive work and service or 'Seva'",
    assessment: [
      { entryKey: 'C_5_1', entryLabel: 'Demonstrates willingness and participation in age-appropriate physical work towards helping others' },
    ],
  },
  {
    s_no: 6,
    domain: 'Socio-Emotional & Ethical Development',
    curricularGoal: 'Children develop a positive regard for the natural environment around them',
    label: 'CG-6: Children develop a positive regard for the natural environment around them',
    assessment: [
      { entryKey: 'C_6_1', entryLabel: 'Shows care for and joy in engaging with all life forms' },
    ],
  },
  {
    s_no: 7,
    domain: 'Cognitive Development',
    curricularGoal: 'Children make sense of the world through observation and logical thinking',
    label: 'CG-7: Children make sense of the world through observation and logical thinking',
    assessment: [
      { entryKey: 'C_7_1', entryLabel: 'Observes and understands different categories of objects and relationships between them' },
      { entryKey: 'C_7_2', entryLabel: 'Observes and understands cause and effect relationships in nature by forming simple hypothesis' },
      { entryKey: 'C_7_3', entryLabel: 'Uses appropriate tools and technology in daily life situations and for learning' },
    ],
  },
  {
    s_no: 8,
    domain: 'Cognitive Development',
    curricularGoal: 'Children develop mathematical understanding and abilities to recognize the world through quantities, shapes, and measures',
    label: 'CG-8: Mathematical Understanding (Numeracy)',
    assessment: [
      { entryKey: 'C_8_1', entryLabel: 'Sorts objects into groups and sub-groups based on more than one property' },
      { entryKey: 'C_8_2', entryLabel: 'Identifies and extends simple patterns in their surroundings, shapes, and numbers' },
      { entryKey: 'C_8_3', entryLabel: 'Counts up to 99 forwards, backwards, in 10s and 20s' },
      { entryKey: 'C_8_4', entryLabel: 'Arranges numbers up to 99 in ascending/descending order' },
      { entryKey: 'C_8_5', entryLabel: 'Recognises and uses numerals to represent quantities up to 99 with understanding of decimal place value system' },
      { entryKey: 'C_8_6', entryLabel: 'Performs addition and subtraction of 2-digit numbers fluently using flexible strategies of composition and decomposition' },
      { entryKey: 'C_8_7', entryLabel: 'Recognises multiplication as repeated addition and division as equal sharing' },
      { entryKey: 'C_8_8', entryLabel: 'Recognises basic geometric shapes and their observable properties' },
      { entryKey: 'C_8_9', entryLabel: 'Performs simple measurements of length, weight and volume of objects in their immediate environment' },
      { entryKey: 'C_8_10', entryLabel: 'Performs simple measurements of time in minutes, hours, day, weeks, and months' },
      { entryKey: 'C_8_11', entryLabel: 'Performs simple transactions using money up to INR 100' },
      { entryKey: 'C_8_12', entryLabel: 'Develops adequate vocabulary for comprehending and expressing concepts related to quantities, shapes, space, and measurements' },
      { entryKey: 'C_8_13', entryLabel: 'Formulates and solves simple mathematical problems related to quantities, shapes, space, and measurements' },
    ],
  },
  {
    s_no: 9,
    domain: 'Language & Literacy Development',
    curricularGoal: 'Children develop effective communication skills for day-to-day interactions in two languages',
    label: 'CG-9: Oral Communication & Language Skills',
    assessment: [
      { entryKey: 'C_9_1', entryLabel: 'Listens to and appreciates songs, rhymes, poems' },
      { entryKey: 'C_9_2', entryLabel: 'Creates simple songs and poems on their own' },
      { entryKey: 'C_9_3', entryLabel: 'Converses fluently and can hold a meaningful conversation' },
      { entryKey: 'C_9_4', entryLabel: 'Understands oral instructions for a complex task and gives clear oral instructions for the same to others' },
      { entryKey: 'C_9_5', entryLabel: 'Comprehends narrated/read-out stories and identifies characters, storyline and what the author wants to say' },
      { entryKey: 'C_9_6', entryLabel: 'Narrates short stories with clear plot and characters' },
      { entryKey: 'C_9_7', entryLabel: 'Knows and uses enough words to carry out day-to-day interactions effectively and can guess meaning of new words' },
    ],
  },
  {
    s_no: 10,
    domain: 'Language & Literacy Development',
    curricularGoal: 'Children develop fluency in reading and writing in Language 1 (L1)',
    label: 'CG-10: Reading & Writing Fluency (L1)',
    assessment: [
      { entryKey: 'C_10_1', entryLabel: 'Develops phonological awareness and blends phonemes/syllables into words and segment words into phonemes/syllables' },
      { entryKey: 'C_10_2', entryLabel: 'Understands basic structure/format of a book, idea of words in print and direction in which they are printed, and recognises basic punctuation marks' },
      { entryKey: 'C_10_3', entryLabel: 'Recognises all the letters of the alphabet (forms of akshara) of the script and uses this knowledge to read and write words' },
      { entryKey: 'C_10_4', entryLabel: 'Reads stories and passages with accuracy and fluency with appropriate pauses and voice modulation' },
      { entryKey: 'C_10_5', entryLabel: 'Reads short stories and comprehends its meaning — identifying characters, storyline and what the author wanted to say — on their own' },
      { entryKey: 'C_10_6', entryLabel: 'Reads short poems and begins to appreciate the poem for its choice of words and imagination' },
      { entryKey: 'C_10_7', entryLabel: 'Reads and comprehends meaning of short news items, instructions and recipes, and publicity material' },
      { entryKey: 'C_10_8', entryLabel: 'Writes a paragraph to express their understanding and experiences' },
      { entryKey: 'C_10_9', entryLabel: 'Shows interest in picking up and reading a variety of children\'s books' },
    ],
  },
  {
    s_no: 11,
    domain: 'Language & Literacy Development',
    curricularGoal: 'Children begin to read and write in Language 2 (L2)',
    label: 'CG-11: Reading & Writing in L2',
    assessment: [
      { entryKey: 'C_11_1', entryLabel: 'Develops phonological awareness and are able to blend phonemes/syllables into words and segment words into phonemes/syllables' },
      { entryKey: 'C_11_2', entryLabel: 'Recognises most frequently occurring letters of the alphabet of the script and uses this knowledge to read and write simple words and sentences' },
    ],
  },
  {
    s_no: 12,
    domain: 'Aesthetic & Cultural Development',
    curricularGoal: 'Children develop abilities and sensibilities in visual and performing arts and express their emotions through art',
    label: 'CG-12: Arts & Cultural Abilities',
    assessment: [
      { entryKey: 'C_12_1', entryLabel: 'Explores and plays with a variety of materials and tools to create two-dimensional and three-dimensional artworks in varying sizes' },
      { entryKey: 'C_12_2', entryLabel: 'Explores and plays with own voice, body, spaces, and a variety of objects to create music, role-play, dance and movement' },
      { entryKey: 'C_12_3', entryLabel: 'Innovates and works imaginatively to express a range of ideas and emotions through the arts' },
      { entryKey: 'C_12_4', entryLabel: 'Works collaboratively in the arts' },
      { entryKey: 'C_12_5', entryLabel: 'Communicates and appreciates a variety of responses while creating and experiencing different forms of art, local culture, and heritage' },
    ],
  },
  {
    s_no: 13,
    domain: 'Positive Learning Habits',
    curricularGoal: 'Children develop habits of learning that allow them to engage actively in formal learning environments like a school classroom',
    label: 'CG-13: Learning Habits for Formal Environments',
    assessment: [
      { entryKey: 'C_13_1', entryLabel: 'Attention and intentional action: Acquires skills to plan, focus attention, and direct activities to achieve specific goals' },
      { entryKey: 'C_13_2', entryLabel: 'Memory and mental flexibility: Develops adequate working memory, mental flexibility, and self-control that would assist them in learning in structured environments' },
      { entryKey: 'C_13_3', entryLabel: 'Observation, wonder, curiosity, and exploration: Observes minute details of objects, wonders, and explores using various senses, tinkers with objects, asks questions' },
      { entryKey: 'C_13_4', entryLabel: 'Classroom norms: Adopts and follows norms with agency and understanding' },
    ],
  },
];

// ─── Domain groupings ────────────────────────────────────────────────────────
export const DOMAIN_COLORS: Record<string, { bg: string; text: string; border: string; accent: string }> = {
  'Physical Development':                  { bg: 'from-emerald-50 to-teal-50',   text: 'text-emerald-700',  border: 'border-emerald-200', accent: '#10b981' },
  'Socio-Emotional & Ethical Development': { bg: 'from-violet-50 to-purple-50',  text: 'text-violet-700',   border: 'border-violet-200',  accent: '#7c3aed' },
  'Cognitive Development':                 { bg: 'from-blue-50 to-indigo-50',    text: 'text-blue-700',     border: 'border-blue-200',    accent: '#3b82f6' },
  'Language & Literacy Development':       { bg: 'from-amber-50 to-orange-50',   text: 'text-amber-700',    border: 'border-amber-200',   accent: '#f59e0b' },
  'Aesthetic & Cultural Development':      { bg: 'from-rose-50 to-pink-50',      text: 'text-rose-700',     border: 'border-rose-200',    accent: '#f43f5e' },
  'Positive Learning Habits':              { bg: 'from-sky-50 to-cyan-50',       text: 'text-sky-700',      border: 'border-sky-200',     accent: '#0ea5e9' },
};

/** Academic domain grouping — for the Academic view */
export const ACADEMIC_DOMAINS = [
  {
    id: 'math',
    label: 'Mathematics',
    icon: '🔢',
    description: 'Numeracy, counting, arithmetic, shapes & measurement',
    cgNumbers: [8],
    accent: '#3b82f6',
    bg: 'from-blue-50 to-indigo-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
  },
  {
    id: 'language_l1',
    label: 'Language Fluency (L1)',
    icon: '📖',
    description: 'Reading, writing, phonics & comprehension in mother tongue',
    cgNumbers: [10],
    accent: '#f59e0b',
    bg: 'from-amber-50 to-orange-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
  },
  {
    id: 'communication',
    label: 'Oral Communication',
    icon: '🗣️',
    description: 'Listening, speaking, storytelling & vocabulary in two languages',
    cgNumbers: [9, 11],
    accent: '#f97316',
    bg: 'from-orange-50 to-red-50',
    border: 'border-orange-200',
    text: 'text-orange-700',
  },
  {
    id: 'physical',
    label: 'Physical Development',
    icon: '🤸',
    description: 'Health, hygiene, sensory awareness & body fitness',
    cgNumbers: [1, 2, 3],
    accent: '#10b981',
    bg: 'from-emerald-50 to-teal-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
  },
  {
    id: 'socio_emotional',
    label: 'Socio-Emotional',
    icon: '🤝',
    description: 'Emotional intelligence, cooperation, values & environment',
    cgNumbers: [4, 5, 6],
    accent: '#7c3aed',
    bg: 'from-violet-50 to-purple-50',
    border: 'border-violet-200',
    text: 'text-violet-700',
  },
  {
    id: 'cognitive',
    label: 'Cognitive & Logical',
    icon: '🧠',
    description: 'Observation, logical thinking, curiosity & problem solving',
    cgNumbers: [7],
    accent: '#6366f1',
    bg: 'from-indigo-50 to-violet-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
  },
  {
    id: 'arts',
    label: 'Arts & Culture',
    icon: '🎨',
    description: 'Visual arts, music, dance & creative expression',
    cgNumbers: [12],
    accent: '#f43f5e',
    bg: 'from-rose-50 to-pink-50',
    border: 'border-rose-200',
    text: 'text-rose-700',
  },
  {
    id: 'learning_habits',
    label: 'Learning Habits',
    icon: '📚',
    description: 'Attention, memory, curiosity, exploration & classroom norms',
    cgNumbers: [13],
    accent: '#0ea5e9',
    bg: 'from-sky-50 to-cyan-50',
    border: 'border-sky-200',
    text: 'text-sky-700',
  },
];

export type RubricScore = 1 | 2 | 3;
export type CompetencyMap = Record<string, RubricScore>;

export const RUBRIC_LABELS: Record<number, string> = { 1: 'Beginner', 2: 'Progressing', 3: 'Proficient' };
export const RUBRIC_COLORS: Record<number, { bg: string; text: string; dot: string }> = {
  1: { bg: 'bg-red-50',    text: 'text-red-600',    dot: 'bg-red-400' },
  2: { bg: 'bg-amber-50',  text: 'text-amber-600',  dot: 'bg-amber-400' },
  3: { bg: 'bg-emerald-50',text: 'text-emerald-700',dot: 'bg-emerald-400' },
};

/** Parse term1_competency / term2_competency JSON string */
export const parseCompetencyMap = (raw: string | null | undefined): CompetencyMap => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const result: CompetencyMap = {};
    for (const [key, val] of Object.entries(parsed)) {
      const n = Number(val);
      if (n === 1 || n === 2 || n === 3) result[key] = n as RubricScore;
    }
    return result;
  } catch {
    return {};
  }
};

/** Get domain-level score (0-100) for a given set of CG numbers */
export const getDomainScore = (cgNumbers: number[], competencyMap: CompetencyMap): number => {
  const cgs = FOUNDATIONAL_SCHEMA.filter(cg => cgNumbers.includes(cg.s_no));
  const scores: number[] = [];
  cgs.forEach(cg => {
    cg.assessment.forEach(ind => {
      const score = competencyMap[ind.entryKey];
      if (score) scores.push((score / 3) * 100);
    });
  });
  if (!scores.length) return 0;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
};

/** Overall competency as percentage from map */
export const getOverallScore = (competencyMap: CompetencyMap): number => {
  const scores = Object.values(competencyMap).map(v => (v / 3) * 100);
  if (!scores.length) return 0;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
};

/** Count Beginner / Progressing / Proficient in a map */
export const countRubricLevels = (competencyMap: CompetencyMap) => {
  let beginner = 0, progressing = 0, proficient = 0;
  Object.values(competencyMap).forEach(v => {
    if (v === 1) beginner++;
    else if (v === 2) progressing++;
    else if (v === 3) proficient++;
  });
  return { beginner, progressing, proficient, total: beginner + progressing + proficient };
};
